/**
 * The By The Numbers section of the Contentful product pages: each of the entry's `byTheNumbers`
 * figures as a large number over its label, then every figure's `citation` in small type. From
 * `lg` up the figures sit in one row; below `lg` they wrap two to a row.
 *
 * Rendered by `components/Contentful/Product.tsx` and `SubProduct.tsx`, with `staticContent` from
 * `content/ryerson/products/productStaticContent.ts` or `subCategoryStaticContent.ts` →
 * `ByTheNumbersStaticContent`. An entry without figures renders the section with no figures.
 * It replaced `./Metrics.tsx`, which has no importer.
 */
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import { Media } from "@ryerson/frontend.next";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { Column, Grid, Row } from "@ryerson/frontend.layout";
import { ContentfulByTheNumbers } from "./Types/Types";

/** One language's fixed copy for the section. */
export type StaticByTheNumbers = {
	/** Section heading; also the source of the section's anchor id (`AllSections`). */
	title: string;
	/** Link text in the page's `SubNavigation` bar. */
	navLabel: string;
};

/** The section's figures from the Contentful entry. `null` or absent renders no figures. */
export type DynamicByTheNumbers = {
	byTheNumbers: ContentfulByTheNumbers[] | null;
};

/** Props of the default export. */
export type ByTheNumbersContentProps = {
	staticContent: StaticByTheNumbers;
	dynamicContent: DynamicByTheNumbers;
};

const Title = styled(Typography)`
	font-weight: 500;
	margin-bottom: 59px;
`;
const Divider = styled.div`
	width: 100%;
	opacity: 0.1;
	margin-bottom: 69px;
	${(props: any) => {
		const { theme } = props;
		return css`
			border-bottom: 1px solid ${theme.colors.primary.header};
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-bottom: 32px;
				margin-top: 20px;
			}
		`;
	}}
`;
const Metric = styled(Typography)`
	${(props: any) => {
		const { theme } = props;
		return css`
			color: ${theme.colors.primary.header};
			@media screen and (max-width: ${theme.breakpoints.lg}) {
				font-size: 37px;
				line-height: 40px;
				font-weight: 500;
				letter-spacing: -0.04em;
			}
			@media screen and (min-width: ${theme.breakpoints.lg}) {
				font-size: 150px;
				line-height: 140px;
				font-weight: 500;
				letter-spacing: -0.06em;
			}
		`;
	}}
`;
const MetricLabel = styled(Typography)`
	${(props: any) => {
		const { theme } = props;
		return css`
			@media screen and (max-width: ${theme.breakpoints.lg}) {
				margin: 10px 0 34px 0;
				width: 70%;
			}
			@media screen and (min-width: ${theme.breakpoints.lg}) {
				margin-bottom: 90px;
				padding-left: 4px;
			}
		`;
	}}
`;

type MetricCardType = {
	blurb: string;
	heading: string;
};

/** One figure: `heading` in the large metric style, `blurb` under it. */
const MetricCard: React.FC<MetricCardType> = ({ blurb, heading }) => {
	const { theme } = useTheme();
	return (
		<FlexItem>
			<Metric theme={theme}>{heading}</Metric>
			<MetricLabel theme={theme} variant="p" size="lg">
				{blurb}
			</MetricLabel>
		</FlexItem>
	);
};

/** The By The Numbers section; the heading is printed from `lg` up only. */
const ByTheNumbersComponent: React.FC<ByTheNumbersContentProps> = ({
	staticContent,
	dynamicContent,
}) => {
	const { theme } = useTheme();
	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection
					vPadding="45px"
					type="split"
					split={{
						splitDirection: "vertical",
						splitColors: [
							{
								color: theme.colors.secondary.background,
								percentage: "56%",
							},
							{
								color: theme.colors.primary.background,
								percentage: "44%",
							},
						],
					}}
				>
					<div>
						<Title variant="h1">{staticContent.title}</Title>
						<Divider theme={theme} />
					</div>
					<Flex justifyContent="space-between">
						{dynamicContent.byTheNumbers?.map(
							(byTheNumber: ContentfulByTheNumbers, index: number) => {
								return (
									<MetricCard
										key={index}
										blurb={byTheNumber.blurb}
										heading={byTheNumber.heading}
									/>
								);
							}
						)}
					</Flex>
					{dynamicContent.byTheNumbers?.map(
						(byTheNumber: ContentfulByTheNumbers, index: number) => {
							return (
								byTheNumber.citation && (
									<Typography
										key={index}
										variant="p"
										size="sm"
										css={css`
											margin: 0;
										`}
									>
										{byTheNumber.citation}
									</Typography>
								)
							);
						}
					)}
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection vPadding="30px" hPadding="0">
					<Grid gutter={0}>
						<Row>
							{dynamicContent.byTheNumbers?.map(
								(byTheNumber: ContentfulByTheNumbers, index: number) => {
									return (
										<Column xs={6} key={index}>
											<MetricCard
												blurb={byTheNumber.blurb}
												heading={byTheNumber.heading}
											/>
										</Column>
									);
								}
							)}
						</Row>
					</Grid>
					{dynamicContent.byTheNumbers?.map(
						(byTheNumber: ContentfulByTheNumbers, index: number) => {
							return (
								byTheNumber.citation && (
									<Typography
										key={index}
										variant="p"
										size="sm"
										css={css`
											margin: 0;
										`}
									>
										{byTheNumber.citation}
									</Typography>
								)
							);
						}
					)}
				</ContentSection>
			</Media>
		</>
	);
};

export default ByTheNumbersComponent;
